// src/features/mensagens/hooks/useMensagemDetalhe.js
// ============================================================================
// 🎣 HOOK — Chat Individual / Detalle de Conversación
// 📦 V5 ENTERPRISE — Scroll Infinito · WhatsApp-Grade · Chat siempre abre
// ============================================================================
//
// REGLAS DE ORO:
// 1. Este hook maneja TODO el estado de datos del chat individual
// 2. El componente solo maneja UI (modales, animaciones, layout)
// 3. Compatible 100% con DataContext actual
// 4. Soporta: ver mensaje único O conversación completa
// 5. V4: Chat SIEMPRE abre — con o sin mensajes previos
// 6. V4: Cliente se busca en DataContext → localStorage → fallback mínimo
// 7. V4: Cero loading infinito — siempre resuelve
// 8. 🔥 V5: Scroll infinito hacia arriba — loadOlderMessages()
// 9. 🔥 V5: Paginación (30 mensajes por carga) — como WhatsApp
// 10. 🔥 V5: Mantiene posición de scroll al cargar mensajes antiguos
// ============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMensagensService } from '../api/mensagensService';
import { validateMensagem, validateReenvio, validateExclusao } from '../utils/validators';
import { formatMessageForClipboard } from '../utils/formatters';
import { MESSAGE_STATUS, MESSAGE_ACTIONS } from '../utils/constants';

// ============================================================================
// 🔥 V5: CONFIGURACIÓN DE PAGINACIÓN
// ============================================================================
const PAGE_SIZE = 30; // Mensajes por carga (WhatsApp carga ~30-50)

// ============================================================================
// 🎯 HOOK PRINCIPAL
// ============================================================================

/**
 * Hook para gestionar el detalle de una conversación o mensaje individual
 * 
 * Modos de uso:
 * 1. Con URL param :conversationId → Carga toda la conversación (modo chat)
 * 2. Con messageId directo → Carga un mensaje individual (modo detalle)
 * 
 * 🔥 V5: Scroll infinito hacia arriba como WhatsApp
 * 
 * @param {Object} options
 * @param {string} [options.messageId] - ID de mensaje individual (opcional)
 * @param {boolean} [options.autoLoad=true] - Cargar al montar
 * @param {string} [options.userId] - ID del usuario (opcional, se resuelve de localStorage)
 * @returns {Object} Estado y acciones del chat
 */
export function useMensagemDetalhe({
  messageId: propMessageId,
  autoLoad = true,
  userId: propUserId,
} = {}) {
  // --------------------------------------------------------------------------
  // 📍 ROUTER
  // --------------------------------------------------------------------------
  const { conversationId, id: paramId } = useParams();
  const navigate = useNavigate();

  // El ID puede venir de props o de la URL
  const targetId = propMessageId || conversationId || paramId;
  
  // 🔥 V4: Determinar modo — si hay propMessageId es modo mensaje, si no es modo conversación
  const isConversationMode = !propMessageId && !!conversationId;

  // --------------------------------------------------------------------------
  // 📦 ESTADO DE DATOS
  // --------------------------------------------------------------------------
  const [message, setMessage] = useState(null);
  const [client, setClient] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // 🔥 V5: Estados de paginación
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [oldestTimestamp, setOldestTimestamp] = useState(null);

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const mountedRef = useRef(true);
  const serviceRef = useRef(null);
  const allMessagesCache = useRef([]); // 🔥 V5: Cache completo para paginación
  const clientIdRef = useRef(null);    // 🔥 V5: Guardar clientId para paginación

  // --------------------------------------------------------------------------
  // 🔐 RESOLVER userId (V2 — Ultra Robusto)
  // --------------------------------------------------------------------------

  const resolvedUserId = useMemo(() => {
    if (propUserId) return propUserId;

    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch {}

    try {
      if (window.firebase?.auth?.currentUser?.uid) {
        return window.firebase.auth.currentUser.uid;
      }
    } catch {}

    try {
      const db = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      const users = db.users || [];
      if (users.length > 0 && users[0]?.id) {
        return users[0].id;
      }
    } catch {}

    return null;
  }, [propUserId]);

  // --------------------------------------------------------------------------
  // 🏗️ INICIALIZACIÓN
  // --------------------------------------------------------------------------
  useEffect(() => {
    serviceRef.current = getMensagensService(resolvedUserId || undefined);

    console.log('🔌 [useMensagemDetalhe V5] Servicio inicializado', {
      hasUserId: !!resolvedUserId,
      userId: resolvedUserId ? `${resolvedUserId.slice(0, 8)}...` : 'no resuelto',
      targetId: targetId || 'sin target',
      mode: isConversationMode ? 'conversación' : 'mensaje',
    });

    return () => {
      mountedRef.current = false;
    };
  }, [resolvedUserId]);

  // --------------------------------------------------------------------------
  // 👤 BUSCAR CLIENTE (3 fuentes: DataContext → localStorage → fallback)
  // 🔥 V4: Función independiente para reutilizar en loadMessage y loadConversation
  // --------------------------------------------------------------------------

  const findClient = useCallback(async (clientId) => {
    const service = serviceRef.current;
    if (!service || !clientId) return null;

    // Fuente 1: DataContext
    try {
      const ctx = await service._getContext();
      if (ctx?.collections?.clients?.read) {
        const clients = await ctx.collections.clients.read();
        const found = Array.isArray(clients)
          ? clients.find(c => c.id === clientId) || null
          : null;
        if (found) {
          console.log('✅ [V5] Cliente encontrado en DataContext:', found.name);
          return found;
        }
      }
    } catch (err) {
      console.warn('⚠️ [V5] DataContext falló:', err.message);
    }

    // Fuente 2: localStorage
    try {
      const db = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      const users = db.users || [];
      
      // Buscar en la DB global
      const globalClients = db.clients || [];
      let found = globalClients.find(c => c.id === clientId) || null;
      
      // Buscar en DBs de usuarios
      if (!found) {
        for (const user of users) {
          const userDb = JSON.parse(localStorage.getItem(`imoveispro_db_user_${user.id}`) || '{}');
          const userClients = userDb.clients || [];
          found = userClients.find(c => c.id === clientId) || null;
          if (found) break;
        }
      }
      
      if (found) {
        console.log('✅ [V5] Cliente encontrado en localStorage:', found.name);
        return found;
      }
    } catch (err) {
      console.warn('⚠️ [V5] localStorage falló:', err.message);
    }

    // Fuente 3: Fallback mínimo (el chat SIEMPRE abre)
    console.log('⚠️ [V5] Cliente no encontrado, usando fallback mínimo para:', clientId);
    return {
      id: clientId,
      name: 'Cliente ' + (clientId?.slice(-6) || ''),
      phone: '',
    };
  }, []);

  // --------------------------------------------------------------------------
  // 🔥 V5: ORDENAR MENSAJES (Más reciente primero para paginación)
  // --------------------------------------------------------------------------

  const sortMessagesByTime = useCallback((messages) => {
    return [...messages].sort((a, b) => {
      const timeA = a.timestamp || a.createdAt || 0;
      const timeB = b.timestamp || b.createdAt || 0;
      return timeB - timeA; // Más reciente primero
    });
  }, []);

  // --------------------------------------------------------------------------
  // 🔥 V5: CARGAR MENSAJES ANTIGUOS (Scroll infinito hacia arriba)
  // --------------------------------------------------------------------------

  /**
   * Carga mensajes más antiguos y los agrega al final de la lista visible
   * @returns {Promise<{loaded: number, hasMore: boolean}>}
   */
  const loadOlderMessages = useCallback(async () => {
    const cache = allMessagesCache.current;
    
    // Si ya mostramos todos, no hay más que cargar
    if (conversationMessages.length >= cache.length) {
      console.log('📋 [V5] No hay más mensajes antiguos');
      setHasMore(false);
      return { loaded: 0, hasMore: false };
    }

    setIsLoadingOlder(true);

    try {
      // Calcular cuántos mensajes agregar
      const currentCount = conversationMessages.length;
      const nextCount = Math.min(currentCount + PAGE_SIZE, cache.length);
      const newMessages = cache.slice(0, nextCount);
      
      // Guardar el mensaje más antiguo para referencia
      const oldest = newMessages[newMessages.length - 1];
      const oldestTime = oldest?.timestamp || oldest?.createdAt || 0;

      if (mountedRef.current) {
        setConversationMessages(newMessages);
        setOldestTimestamp(oldestTime);
        setHasMore(nextCount < cache.length);
        
        console.log(`📋 [V5] Cargados ${nextCount} de ${cache.length} mensajes (${nextCount - currentCount} nuevos)`);
      }

      setIsLoadingOlder(false);
      return { loaded: nextCount - currentCount, hasMore: nextCount < cache.length };
    } catch (err) {
      console.error('❌ [V5] Error cargando mensajes antiguos:', err);
      if (mountedRef.current) {
        setIsLoadingOlder(false);
      }
      return { loaded: 0, hasMore: false };
    }
  }, [conversationMessages.length]);

  // --------------------------------------------------------------------------
  // 📥 CARGA DE DATOS
  // --------------------------------------------------------------------------

  /**
   * Carga un mensaje individual por ID
   */
  const loadMessage = useCallback(async (msgId) => {
    const service = serviceRef.current;
    if (!service || !msgId) {
      console.warn('⚠️ [useMensagemDetalhe] loadMessage: sin servicio o msgId');
      if (mountedRef.current) setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📥 [V5] Cargando mensaje:', msgId);
      const msg = await service.getMessageById(msgId);

      if (!msg) {
        if (mountedRef.current) {
          setError('Mensaje no encontrado');
          setMessage(null);
          setLoading(false);
        }
        return;
      }

      // Buscar cliente
      const clientData = await findClient(msg.clientId || targetId);
      
      // Si el cliente no tiene nombre real, usar el del mensaje
      if (clientData && clientData.name === 'Cliente' && msg.clientName) {
        clientData.name = msg.clientName;
      }

      // 🔥 V5: Cargar conversación completa con paginación
      let allMessages = [];
      if (msg.clientId) {
        try {
          allMessages = await service.getMessagesByClientId(msg.clientId);
          allMessages = sortMessagesByTime(allMessages);
        } catch (convErr) {
          allMessages = [msg];
        }
      } else {
        allMessages = [msg];
      }

      // 🔥 V5: Guardar en cache y mostrar primera página
      allMessagesCache.current = allMessages;
      const firstPage = allMessages.slice(0, PAGE_SIZE);

      if (mountedRef.current) {
        setMessage(msg);
        setClient(clientData);
        setConversationMessages(firstPage);
        setHasMore(allMessages.length > PAGE_SIZE);
        setOldestTimestamp(firstPage[firstPage.length - 1]?.timestamp || null);
        clientIdRef.current = msg.clientId;
        setLoading(false);
        console.log(`✅ [V5] Chat cargado: ${firstPage.length}/${allMessages.length} mensajes`);
      }
    } catch (err) {
      console.error('❌ [useMensagemDetalhe] Error:', err);
      if (mountedRef.current) {
        setError(err.message || 'Error al cargar');
        setLoading(false);
      }
    }
  }, [targetId, findClient, sortMessagesByTime]);

  /**
   * 🔥 V5: Carga conversación por clientId — SIEMPRE resuelve + Paginación
   * Busca cliente en 3 fuentes, mensajes en 2 fuentes
   */
  const loadConversation = useCallback(async (clientId) => {
    const service = serviceRef.current;
    if (!service || !clientId) {
      console.warn('⚠️ [useMensagemDetalhe] loadConversation: sin servicio o clientId');
      if (mountedRef.current) setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📥 [V5] Cargando conversación para cliente:', clientId);
      clientIdRef.current = clientId;

      // 🔥 V4: Buscar cliente — SIEMPRE resuelve (nunca null)
      const clientData = await findClient(clientId);

      // 🔥 V5: Cargar TODOS los mensajes
      let allMessages = [];
      try {
        allMessages = await service.getMessagesByClientId(clientId);
        allMessages = sortMessagesByTime(allMessages);
        console.log(`📋 [V5] ${allMessages.length} mensajes totales`);
      } catch (msgErr) {
        console.warn('⚠️ [V5] DataContext mensajes falló, buscando en localStorage:', msgErr.message);
        
        // Buscar en localStorage como fallback
        try {
          const db = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
          const users = db.users || [];
          const allLocalMessages = db.messages || [];
          
          for (const user of users) {
            const userDb = JSON.parse(localStorage.getItem(`imoveispro_db_user_${user.id}`) || '{}');
            if (userDb.messages) allLocalMessages.push(...userDb.messages);
          }
          
          allMessages = allLocalMessages.filter(m => m.clientId === clientId);
          allMessages = sortMessagesByTime(allMessages);
          console.log(`📋 [V5] ${allMessages.length} mensajes en localStorage`);
        } catch (localErr) {
          allMessages = [];
        }
      }

      // 🔥 V5: Guardar cache completo, mostrar primera página
      allMessagesCache.current = allMessages;
      const firstPage = allMessages.slice(0, PAGE_SIZE);
      const oldest = firstPage[firstPage.length - 1];

      if (mountedRef.current) {
        setClient(clientData);
        setConversationMessages(firstPage);
        setMessage(null);
        setHasMore(allMessages.length > PAGE_SIZE);
        setOldestTimestamp(oldest?.timestamp || oldest?.createdAt || null);
        setLoading(false);
        console.log(`✅ [V5] Conversación lista: ${firstPage.length}/${allMessages.length} mensajes, cliente: ${clientData?.name}`);
      }
    } catch (err) {
      console.error('❌ [V5] Error crítico:', err);
      if (mountedRef.current) {
        // 🔥 V4: Incluso si todo falla, mostrar chat vacío (nunca loading infinito)
        setClient({ id: clientId, name: 'Cliente', phone: '' });
        setConversationMessages([]);
        allMessagesCache.current = [];
        setHasMore(false);
        setError(null);
        setLoading(false);
      }
    }
  }, [findClient, sortMessagesByTime]);

  // --------------------------------------------------------------------------
  // 🚀 CARGA INICIAL
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!autoLoad || !targetId || !resolvedUserId) return;

    if (isConversationMode) {
      console.log('🔄 [V5] Modo conversación → loadConversation:', targetId);
      loadConversation(targetId);
    } else if (propMessageId) {
      console.log('🔄 [V5] Modo mensaje individual → loadMessage:', targetId);
      loadMessage(targetId);
    } else {
      console.log('🔄 [V5] Modo genérico → loadConversation:', targetId);
      loadConversation(targetId);
    }
  }, [autoLoad, targetId, propMessageId, resolvedUserId, isConversationMode, loadMessage, loadConversation]);

  // --------------------------------------------------------------------------
  // ✏️ EDITAR MENSAJE
  // --------------------------------------------------------------------------

  const editMessage = useCallback(async (messageId, newText) => {
    const service = serviceRef.current;
    if (!service || !messageId || !newText) return;

    const validation = validateMensagem(newText);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    try {
      setActionLoading('edit');
      await service.editMessage(messageId, validation.sanitized);

      const updated = validation.sanitized;
      if (mountedRef.current) {
        setMessage(prev => prev?.id === messageId ? { ...prev, message: updated, edited: true } : prev);
        setConversationMessages(prev =>
          prev.map(m => m.id === messageId ? { ...m, message: updated, edited: true } : m)
        );
      }

      setActionLoading(null);
    } catch (err) {
      console.error('❌ [V5] Error editando:', err);
      setActionLoading(null);
      throw err;
    }
  }, []);

  // --------------------------------------------------------------------------
  // 🔄 REENVIAR MENSAJE
  // --------------------------------------------------------------------------

  const resendMessage = useCallback(async (originalMessageId, { clientId, clientName, clientPhone }) => {
    const service = serviceRef.current;
    if (!service || !originalMessageId) return;

    const validation = validateReenvio({
      messageId: originalMessageId,
      targetClientId: clientId,
      targetPhone: clientPhone,
    });
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    try {
      setActionLoading('resend');
      const newMessage = await service.resendMessage(originalMessageId, {
        clientId,
        clientName: clientName || 'Desconhecido',
        clientPhone: clientPhone || '',
      });
      setActionLoading(null);
      return newMessage;
    } catch (err) {
      console.error('❌ [V5] Error reenviando:', err);
      setActionLoading(null);
      throw err;
    }
  }, []);

  // --------------------------------------------------------------------------
  // 🗑️ ELIMINAR MENSAJE (con soporte para eliminar del cache)
  // --------------------------------------------------------------------------

  const deleteMessage = useCallback(async (messageId) => {
    const service = serviceRef.current;
    if (!service || !messageId) return;

    const validation = validateExclusao({ type: 'message', id: messageId });
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    try {
      setActionLoading('delete');
      await service.deleteMessage(messageId);

      if (mountedRef.current) {
        // Eliminar de la vista actual
        setConversationMessages(prev => prev.filter(m => m.id !== messageId));
        // Eliminar del cache
        allMessagesCache.current = allMessagesCache.current.filter(m => m.id !== messageId);
        
        if (message?.id === messageId) {
          setMessage(null);
        }
      }

      setActionLoading(null);
    } catch (err) {
      console.error('❌ [V5] Error eliminando:', err);
      setActionLoading(null);
      throw err;
    }
  }, [message]);

  // --------------------------------------------------------------------------
  // ✅ MARCAR COMO LEÍDO
  // --------------------------------------------------------------------------

  const markAsRead = useCallback(async (messageId) => {
    const service = serviceRef.current;
    if (!service || !messageId) return;

    try {
      await service.markAsRead(messageId);

      if (mountedRef.current) {
        const now = Date.now();
        setMessage(prev => prev?.id === messageId ? { ...prev, status: MESSAGE_STATUS.READ, readAt: now } : prev);
        setConversationMessages(prev =>
          prev.map(m => m.id === messageId ? { ...m, status: MESSAGE_STATUS.READ, readAt: now } : m)
        );
      }
    } catch (err) {
      console.error('❌ [V5] Error marcando leído:', err);
    }
  }, []);

  // --------------------------------------------------------------------------
  // 📋 COPIAR AL PORTAPAPELES
  // --------------------------------------------------------------------------

  const copyToClipboard = useCallback(async (msg) => {
    const target = msg || message;
    if (!target) return false;

    try {
      const text = formatMessageForClipboard(target);
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const text = formatMessageForClipboard(target);
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    }
  }, [message]);

  // --------------------------------------------------------------------------
  // 📤 COMPARTIR (Web Share API)
  // --------------------------------------------------------------------------

  const shareMessage = useCallback(async (msg) => {
    const target = msg || message;
    if (!target) return false;

    const text = target.message || target.content?.body || '';

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Mensagem de ${target.clientName || 'Cliente'}`,
          text: text,
        });
        return true;
      } catch {
        return false;
      }
    }

    return copyToClipboard(target);
  }, [message, copyToClipboard]);

  // --------------------------------------------------------------------------
  // 📥 DESCARGAR MENSAJE
  // --------------------------------------------------------------------------

  const downloadMessage = useCallback((msg) => {
    const target = msg || message;
    if (!target) return;

    const text = formatMessageForClipboard(target);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mensagem_${target.id || 'download'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [message]);

  // --------------------------------------------------------------------------
  // 🔙 NAVEGACIÓN
  // --------------------------------------------------------------------------

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goToConversation = useCallback((clientId) => {
    navigate(`/mensagens/${clientId}`);
  }, [navigate]);

  // --------------------------------------------------------------------------
  // 📊 PERMISOS DE ACCIÓN
  // --------------------------------------------------------------------------

  const permissions = useMemo(() => {
    return {
      [MESSAGE_ACTIONS.VIEW]: true,
      [MESSAGE_ACTIONS.EDIT]: true,
      [MESSAGE_ACTIONS.RESEND]: true,
      [MESSAGE_ACTIONS.DELETE]: true,
      [MESSAGE_ACTIONS.COPY]: true,
      [MESSAGE_ACTIONS.FORWARD]: true,
      [MESSAGE_ACTIONS.STAR]: true,
    };
  }, []);

  // --------------------------------------------------------------------------
  // 📊 ESTADÍSTICAS DE LA CONVERSACIÓN
  // --------------------------------------------------------------------------

  const conversationStats = useMemo(() => {
    const msgs = allMessagesCache.current.length > 0 ? allMessagesCache.current : conversationMessages;
    if (!msgs.length) return null;

    const inbound = msgs.filter(m => m.direction === 'inbound' || m.direction === 'received');
    const outbound = msgs.filter(m => m.direction === 'outbound' || m.direction === 'sent');
    const sorted = [...msgs].sort((a, b) => (a.timestamp || a.createdAt || 0) - (b.timestamp || b.createdAt || 0));
    const firstMsg = sorted[0];
    const lastMsg = sorted[sorted.length - 1];

    return {
      total: msgs.length,
      inbound: inbound.length,
      outbound: outbound.length,
      unread: msgs.filter(m => m.status !== MESSAGE_STATUS.READ).length,
      firstContact: firstMsg?.timestamp || firstMsg?.createdAt || null,
      lastContact: lastMsg?.timestamp || lastMsg?.createdAt || null,
      duration: (firstMsg?.timestamp || firstMsg?.createdAt) && (lastMsg?.timestamp || lastMsg?.createdAt)
        ? (lastMsg.timestamp || lastMsg.createdAt) - (firstMsg.timestamp || firstMsg.createdAt)
        : null,
    };
  }, [conversationMessages]);

  // --------------------------------------------------------------------------
  // 📤 RETORNO
  // --------------------------------------------------------------------------

  return {
    // Datos
    message,
    client,
    conversationMessages,
    conversationStats,

    // Estado
    loading,
    error,
    actionLoading,

    // 🔥 V5: Paginación
    hasMore,
    isLoadingOlder,
    loadOlderMessages,

    // Permisos
    permissions,

    // Acciones
    loadMessage,
    loadConversation,
    editMessage,
    resendMessage,
    deleteMessage,
    markAsRead,
    copyToClipboard,
    shareMessage,
    downloadMessage,

    // Navegación
    goBack,
    goToConversation,

    // Helpers
    isConversationMode,
    isEmpty: !loading && conversationMessages.length === 0,
  };
}

// ============================================================================
// 📤 EXPORTACIÓN DEFAULT
// ============================================================================

export default useMensagemDetalhe;